import _ from "lodash";
import React from "react";

// UI components
import UIModal from "UIComponents/UIModal";
import Loading from "UIComponents/Loading";
import I18nHOC from "UIComponents/I18nHOC";
import DialogueBoxV2 from "UIComponents/DialogueBoxV2";
import UIBaseComponent from "UIComponents/UIBaseComponent";

// Redux Thunks
import {
  writeAssessmentToolInLocal,
  updateAssessmentTool,
  createAssessmentTool,
  deleteAssessmentTool,
} from "AssessmentToolSetup/modules/AssessmentToolSetupModule";
import { upsertDpAssessmentRubricsV2InAssessment } from "modules/AssessmentTool/AssessmentToolModule";

// Queries
import { getAssessmentToolNodeDetailsQuery } from "modules/CommonQuery";
import { getAssessmentToolNodeDetailsFromCache } from "modules/CommonGraphqlHelpers";
import { getAssessmentLearningCourseDetailsFromCache } from "Assessments/modules/AssessmentGraphqlHelpers";

import { getDpAssessmentRubricQuery } from "modules/AssessmentTool/AssessmentToolQuery";
import { getDpAssessmentRubricFromCache } from "modules/AssessmentTool/AssessmentToolGraphqlHelpers";

// App Components
import { AssessmentToolChooseListV2 } from "UnitPlans/components/LibraryCommonComponents";
import RubricTable from "Rubric/components/RubricTable";
import SinglePointRubricTable from "SinglePointRubric/components/SinglePointRubricTable";
import ChecklistTable from "Checklist/components/ChecklistTable";
import Anecdotal from "../AssessmentTool/Anecdotal";
import Exemplar from "../AssessmentTool/Exemplar";
import MYPObjectiveRubric from "../AssessmentTool/MYPObjectiveRubric";
import AutomaticIBScaleTool from "../AssessmentIBScaleTool/AutomaticIBScaleTool";
import AutomaticIBScaleToolV2 from "../AssessmentIBScaleTool/AutomaticIBScaleToolV2";
import StandardsSelectionModal from "AppComponents/StandardsSelectionModal";
import { getCategoryObj } from "../AssessmentCategory/utils";
import CriteriaRubricAssessment from "AppComponents/CriteriaRubricAssessment";
import StandardRubric from "AppComponents/StandardRubric";
import GradeScaleDetailModal from "AppComponents/AssessmentGradeScaleTool/components/GradeScaleDetailModal";

// Services
import {
  createAttachment,
  deleteAttachment,
  getMYPObjectiveRubricOptions,
} from "modules/Services";
import { updateSettings } from "modules/ConfigurationThunks";
import { updateField } from "modules/FieldUpdateThunks";

// External lib.
import { graphql, compose } from "lib/apolloClient";
import { connect } from "react-redux";
import classNames from "classnames";

// Design System
import { IconButton, Button, Tooltip, ToggleSwitch } from "@toddle-edu/ds-web";
import { CloseOutlined, ChevronLeftOutlined } from "@toddle-edu/ds-icons";

// Module
import {
  removeAssessmentTool,
  updateAssessmentInRedux,
  registerAssessmentToolUpdateActivity,
} from "Assessments/modules/AssessmentModule";
import { bulkCreateStandardRubricCriteria } from "AppComponents/StandardRubric/modules/StandardRubricModule";
import { getCourseDetailsFromCache } from "Teacher/routes/Courses/routes/Course/modules/CourseGraphqlHelpers";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";
import { getSettingValue } from "modules/PermissionModule";

// Utils
import { ASSESSMENT_TOOLS } from "@toddle-edu/common-utils";
import {
  getIsAssessmentToolEmpty,
  getDPCriteriaRubricHeaderSubtext,
  SKIP_ASSESSMENT_TOOL_NODE_TOOLS,
  getIsIBGradeScaleTool,
} from "ClassRoom/modules/utils";
import { getIsIBGradeScaleV2ForNewGradingSetupEnabled } from "Utils/ACLStoreUtils";
import {
  getIsCriterionSelected,
  getCheckboxState,
  getUpdatedCriteria,
  getSelectedAssessmentTool,
  getIsDPRubricGlobal,
} from "./Utils";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import {
  getFieldValue,
  getResolvedMinimalTreeSet,
} from "Assessments/modules/Utils";
import {
  DP_ASSESSMENT_RUBRIC,
  GRADE_SCALE_TOOL,
  MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC,
  SCORE_BASED_RUBRIC,
  STANDARD_BASED_RUBRIC,
} from "Constants/stringConstants";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getIsLearningCoursesEnabled } from "Teacher/routes/LearningCourses/modules/Utils";

// Scss
import classes from "./AssessmentToolV2.scss";

const DIALOGUES_INFO = {
  SAVE_TEMPLATE: {
    title: ({ t, label }) =>
      t("common:save_this_template_with_label", { label }),
    message: ({ t }) => t("common:dicard_the_changes_warning_msg"),
    confirmButtonText: t => t("common:add"),
    cancelButtonText: t => t("common:cancel"),
  },
  DISCARD: {
    title: ({ t, label }) => t("common:discard_label", { label }),
    message: ({ t, label }) => t("common:action_cannot_be_undone", { label }),
    confirmButtonText: t => t("common:discard"),
    cancelButtonText: t => t("common:continue_editing_agree"),
  },
};

const getPromptDetail = t => {
  return {
    placeholder: t("unitPlan:enter_template_title"),
    label: t("common:title"),
    checkValidation: true,
  };
};

const getAnecdotalOptions = () => {
  return [
    { label: "Notes", value: "not" },
    { label: "Notes with photos", value: "notpho" },
    { label: "Notes with videos", value: "notvid" },
  ];
};

const getInitialTogglePreference = ({
  unitPlan,
  toolType,
  assessmentFields,
  unitPlanFields,
}) => {
  if (
    _.isEmpty(unitPlan) ||
    !_.includes(
      ["MYP_OBJECTIVE_RUBRIC", "MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC"],
      toolType
    )
  ) {
    return false;
  }

  const fieldKey =
    toolType === "MYP_OBJECTIVE_RUBRIC"
      ? "objectivesMYP"
      : "interdisciplinaryCriteriaMYP";

  const resolvedValue = getFieldValue({
    fields: assessmentFields,
    uid: fieldKey,
  });

  if (_.isEmpty(resolvedValue)) {
    return true;
  }

  const unitPlanNodes = getResolvedMinimalTreeSet({
    uid: fieldKey,
    fields: unitPlanFields,
  });

  return _.every(
    resolvedValue,
    id => !_.isEmpty(_.find(unitPlanNodes, { id }))
  );
};

const styles = {
  button1Props: {
    variant: "neutral",
    type: "outlined",
  },
  anecdotalStyles: {
    listItemStyle: {
      padding: "8px 0",
    },
  },
};

const sortInitialState = {
  columnKey: "lastEdited",
  direction: "DESC",
  entity: "UPDATED_AT",
  sortEntity: "UPDATED_AT",
};

class AssessmentToolV2 extends UIBaseComponent {
  constructor(props) {
    super(props);

    const {
      t,
      isCreateMode,
      assessmentToolHasTemplate,
      assessmentToolTypeLocale,
      assessmentToolModalMode,
      unitPlan,
      toolType,
      unitPlanFields,
      assessmentFields,
      learningCourseId,
    } = this.props;

    this.state = {
      isLoading: false,
      currentDialogueAction: { type: "", params: {} },
      headerDetails: {
        title: assessmentToolTypeLocale,
        subtext: "",
        showBackButton: false,
        showCloseButton: true,
        backButtonClickAction: null,
        disableCloseButton: _.startsWith(assessmentToolModalMode, "create_new"),
        showOnlyUnitSelections: getInitialTogglePreference({
          unitPlan,
          toolType,
          assessmentFields,
          unitPlanFields,
        }),
        isTemplateSelectionStep: false,
      },
      footerDetails: {
        showFooter: !_.startsWith(assessmentToolModalMode, "use_from_template"),
        button1Label: t("classRoom:save_as_template"),
        button2Label: t("common:cancel"),
        button3Label: t("common:create"),
        showButton1: assessmentToolHasTemplate,
        showButton2: true,
        showButton3: isCreateMode,
        button1ClickAction: null,
        button2ClickAction: null,
        button3ClickAction: null,
        button1Variant: "progressive",
        button1Type: "inline",
        legacyButton1Variant: "inline-progressive",
        button3Variant: "primary",
        disableButton2: _.startsWith(assessmentToolModalMode, "create_new"),
        disableButton3: false,
        button3DisabledTooltip: "",
      },
      templateSearchText: "",
      templateCreatedBy: [],
      selectedCriteria: [],
      filterConfig: [sortInitialState],
      showLearningCourseTools: false,
      convertedScoreBasedRubricId: null, // when user chooses "Rubric" from "Score Based Rubric" templates, then converted "Score Based Rubric" will be stored here
    };
  }

  componentDidMount = () => {
    const {
      isCreateMode,
      assessmentToolModalMode,
      assessmentToolType,
    } = this.props;

    // in case of create new, call mutation;

    if (isCreateMode && assessmentToolType !== STANDARD_BASED_RUBRIC) {
      this.createAssessmentTool();
      this.updateFooterDetails({
        button1ClickAction: this.onClickToolSave,
        button3ClickAction: this.toggleAssessmentToolModal,
        disableButton3: this.shouldDisableButton3(),
      });

      // when a new tool is created show it in edit mode
      this.updateAssessmentToolMode("edit");
    } else {
      switch (assessmentToolModalMode) {
        case "view_tool":
          this.viewTool();
          break;
        case "edit_tool":
          this.editTool();
          break;
      }
    }
  };

  componentWillUnmount = () => {
    const {
      updateSettings,
      assessmentToolTemplateUserFilterPreference,
      assessmentToolType,
      curriculumProgramId,
    } = this.props;

    const { templateCreatedBy } = this.state;

    const curriculumWiseUserPreference = _.get(
      assessmentToolTemplateUserFilterPreference,
      curriculumProgramId,
      {}
    );
    const newFilterPreference = {
      ...curriculumWiseUserPreference,
      [assessmentToolType]: { createdBy: templateCreatedBy },
    };

    const newSettingValue = JSON.stringify({
      ...assessmentToolTemplateUserFilterPreference,
      [curriculumProgramId]: newFilterPreference,
    });

    if (
      newSettingValue !==
      JSON.stringify(assessmentToolTemplateUserFilterPreference)
    ) {
      // Update the settings with the new createdBy filter
      try {
        updateSettings({
          settings: [
            {
              name: "AssessmentToolTemplateUserFilterPreference",
              value: newSettingValue,
            },
          ],
          showToastMsg: false,
        });
      } catch (err) {
        console.error(err);
      }
    }
  };

  componentDidUpdate = prevProps => {
    const {
      assessmentTools: prevAssessmentTools,
      assessmentTool: prevAssessmentTool,
      assessmentFields: prevAssessmentFields,
    } = prevProps;

    const {
      assessmentTools,
      updateAssessmentInRedux,
      isCreateMode,
      assessmentTool,
      assessmentToolModalMode,
      assessmentToolType,
      toolId,
      createNew,
      assessmentFields,
    } = this.props;

    // when new assessment tool is created store its type and id in redux
    if (isCreateMode && prevAssessmentTools !== assessmentTools) {
      const node = _.get(_.last(assessmentTools), "node", {});
      const assessmentToolType = _.get(node, "assessmentToolType", "");
      const assessmentToolId = _.get(node, "assessmentToolItem.id", "");

      updateAssessmentInRedux({
        assessmentToolId,
        assessmentToolType,
      });
    }

    // set footer cancel action to remove tool if isAssessmentToolEmpty else show dialogue box
    if (
      _.startsWith(assessmentToolModalMode, "create_new") &&
      prevAssessmentTool !== assessmentTool &&
      !_.isEmpty(assessmentTool)
    ) {
      const isAssessmentToolEmpty = getIsAssessmentToolEmpty({
        assessmentToolType,
        assessmentTool,
      });

      this.updateFooterDetails({
        disableButton2: false,
        button2ClickAction: async () => {
          if (isAssessmentToolEmpty) {
            if (createNew) {
              await this.removeTool({
                toolId,
                toolType: assessmentToolType,
              });
            }
            this.toggleAssessmentToolModal();
          } else {
            this.updateCurrentDialogueBoxAction({
              type: "DISCARD",
              params: {
                toolId,
                toolType: assessmentToolType,
              },
            });
          }
        },
      });

      this.updateHeaderDetails({
        disableCloseButton: false,
      });
    }

    // disable create/done button if no criteria is selected in myp rubric
    if (
      !_.isEqual(assessmentFields, prevAssessmentFields) ||
      !_.isEqual(assessmentTool, prevAssessmentTool)
    ) {
      this.updateFooterDetails({
        disableButton3: this.shouldDisableButton3(),
      });
    }
  };

  handleLearningCourseToggle = () => {
    this.setState(prevState => ({
      showLearningCourseTools: !prevState.showLearningCourseTools,
    }));
  };

  onChangeConvertedScoreBasedRubricId = id => {
    this.setState({ convertedScoreBasedRubricId: id });
  };

  createAssessmentTool = async params => {
    const { extraParams = {} } = params || {};

    const {
      createAssessmentTool,
      toolType,
      parentId,
      parentType,
      filters,
      assessmentId,
      portalType,
    } = this.props;

    return await createAssessmentTool({
      type: toolType,
      parentId,
      parentType,
      isGlobal: false,
      filters,
      extraParams: {
        assessmentId: assessmentId,
        ...extraParams,
      },
      portalType,
    });
  };

  updateAssessmentTool = ({ params, isDebounce }) => {
    const { updateAssessmentTool, type, toolId, updateField, t } = this.props;
    updateField({
      key: toolId,
      params: {
        id: toolId,
        params,
        type,
      },
      type,
      isDebounce,
      func: updateAssessmentTool,
      t,
    });
  };

  saveAssessmentTool = async title => {
    const {
      toolId,
      type,
      createAssessmentTool,
      filters,
      curriculumProgramId,
      assessmentId,
      portalType,
    } = this.props;
    await createAssessmentTool({
      copyToolId: toolId,
      title,
      type,
      mode: "COPY",
      filters,
      curriculumProgramId,
      extraParams: {
        assessmentId: assessmentId,
      },
      portalType,
    });
  };

  onCopyTool = async id => {
    const {
      assessmentToolType,
      createAssessmentTool,
      parentId,
      parentType,
      filters,
      assessmentId,
      portalType,
    } = this.props;

    const { selectedCriteria } = this.state;

    switch (assessmentToolType) {
      case "DP_ASSESSMENT_RUBRIC": {
        const assessmentToolData = [
          {
            parentId,
            parentType,
            toolType: assessmentToolType,
            sourceRubricId: id,
            addedObjectiveIds: [...selectedCriteria],
          },
        ];
        await createAssessmentTool({
          type: assessmentToolType,
          assessmentToolData,
          portalType,
        });
        break;
      }
      default:
        await createAssessmentTool({
          copyToolId: id,
          type: assessmentToolType,
          isGlobal: false,
          mode: "COPY",
          parentId,
          parentType,
          filters,
          extraParams: {
            assessmentId: assessmentId,
          },
          portalType,
        });
    }
  };

  createAttachment = attachment => {
    const { createAttachment, toolId, type } = this.props;
    createAttachment({
      attachment,
      nodeId: toolId,
      type,
      parentType: type,
    });
  };

  deleteAttachment = attachmentId => {
    const { deleteAttachment, toolId, type } = this.props;
    deleteAttachment({
      attachmentId,
      nodeId: toolId,
      type,
    });
  };

  writeAssessmentToolInLocal = ({ params }) => {
    const { writeAssessmentToolInLocal, type, toolId } = this.props;
    writeAssessmentToolInLocal({ type, id: toolId, params });
  };

  shouldDisableButton3 = () => {
    const { assessmentFields = [], toolType, assessmentTool } = this.props;

    let disabled = false;
    let fieldUID = "";

    if (toolType === "MYP_OBJECTIVE_RUBRIC") {
      fieldUID = "objectivesMYP";
    }

    if (toolType === "MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC") {
      fieldUID = "interdisciplinaryCriteriaMYP";
    }

    // disable create button if no criteria is selected from myp rubric
    if (fieldUID) {
      const fieldValue = _.find(assessmentFields, field => {
        return _.get(field, "uid") === fieldUID;
      })?.value;

      disabled = _.isEmpty(fieldValue) || _.isEmpty(assessmentTool);
    }

    return disabled;
  };

  onClickToolSave = () =>
    this.setState({
      currentDialogueAction: {
        type: "SAVE_TEMPLATE",
      },
    });

  updateHeaderDetails = params =>
    this.setState(prevState => ({
      headerDetails: { ...prevState.headerDetails, ...params },
    }));

  updateFooterDetails = params =>
    this.setState(prevState => ({
      footerDetails: { ...prevState.footerDetails, ...params },
    }));

  updateFilterConfig = filterConfig => {
    this.setState(prevState => ({ ...prevState, filterConfig }));
  };

  updateAssessmentToolMode = assessmentToolMode => {
    const { updateAssessmentToolData } = this.props;
    updateAssessmentToolData({
      assessmentToolMode,
    });
  };

  editDPCriteriaRubric = () => {
    const { updateAssessmentToolData } = this.props;
    updateAssessmentToolData({
      assessmentToolModalMode: "edit_tool",
    });
    this.editTool();
  };

  viewTool = () => {
    const {
      t,
      mode,
      assessmentToolHasTemplate,
      assessmentToolTypeLocale,
      assessmentToolType,
      subjectName,
      assessmentTools,
    } = this.props;
    const isDPRubricGlobal = getIsDPRubricGlobal({
      assessmentToolType,
      assessmentTools,
    });

    this.updateAssessmentToolMode("view");

    this.updateFooterDetails({
      showFooter: mode !== "view",
      showButton1:
        assessmentToolHasTemplate ||
        (assessmentToolType === "DP_ASSESSMENT_RUBRIC" && !isDPRubricGlobal),
      showButton2: false,
      showButton3: assessmentToolHasTemplate
        ? false
        : assessmentToolType === "DP_ASSESSMENT_RUBRIC"
        ? false
        : true,
      button1Label:
        assessmentToolHasTemplate ||
        assessmentToolType === "DP_ASSESSMENT_RUBRIC"
          ? t("common:edit_with_label", {
              label: assessmentToolTypeLocale,
            })
          : null,
      button3Label: !assessmentToolHasTemplate && t("common:done"),
      button3Variant: "progressive",
      legacyButton1Variant: "outlined-subtle",
      button1Variant: "neutral",
      button1Type: "outlined",
      button1ClickAction: assessmentToolHasTemplate
        ? this.editTool
        : assessmentToolType === "DP_ASSESSMENT_RUBRIC"
        ? this.editDPCriteriaRubric
        : null,
      button3ClickAction:
        !assessmentToolHasTemplate && this.toggleAssessmentToolModal,
    });

    const selectedAssessmentTool = getSelectedAssessmentTool({
      assessmentTools,
      assessmentToolType,
    });

    this.updateHeaderDetails({
      showCloseButton:
        assessmentToolHasTemplate ||
        mode === "view" ||
        assessmentToolType === "DP_ASSESSMENT_RUBRIC",
      title:
        assessmentToolType === "DP_ASSESSMENT_RUBRIC"
          ? t("common:dp_assessment_criteria_for_label", {
              label: subjectName,
            }) || ""
          : assessmentToolTypeLocale,
      subtext:
        assessmentToolType === "DP_ASSESSMENT_RUBRIC"
          ? getDPCriteriaRubricHeaderSubtext({
              assessmentTool: selectedAssessmentTool,
              t,
            })
          : "",
    });
  };

  onClickButton3 = async () => {
    const {
      assessmentToolType,
      assessmentTool,
      parentId,
      parentType,
      toolId,
      portalType,
      upsertDpAssessmentRubricsV2InAssessment,
    } = this.props;

    const { selectedCriteria } = this.state;

    switch (assessmentToolType) {
      case "DP_ASSESSMENT_RUBRIC": {
        const criteria = _.get(assessmentTool, "criteria", []);
        const criteriaIds = _.map(criteria, "id");
        const addedObjectiveIds = _.difference(selectedCriteria, criteriaIds);
        const removedObjectiveIds = _.difference(criteriaIds, selectedCriteria);
        if (!_.isEmpty(addedObjectiveIds) || !_.isEmpty(removedObjectiveIds)) {
          const assessmentToolData = [
            {
              parentId,
              parentType,
              toolType: assessmentToolType,
              rubricId: toolId,
              addedObjectiveIds,
              removedObjectiveIds,
            },
          ];
          await upsertDpAssessmentRubricsV2InAssessment({
            input: assessmentToolData,
            portalType,
          });
        }
        break;
      }
    }
    this.toggleAssessmentToolModal();
  };

  editTool = () => {
    const {
      t,
      mode,
      assessmentToolHasTemplate,
      subjectName,
      assessmentTools,
      assessmentToolType,
      assessmentToolTypeLocale,
    } = this.props;

    this.updateAssessmentToolMode("edit");

    this.updateFooterDetails({
      showFooter: mode !== "view",
      showButton1: assessmentToolHasTemplate,
      button1Label: t("classRoom:save_as_template"),
      showButton2: false,
      showButton3: true,
      button3Label: t("common:done"),
      button3Variant: "progressive",
      button3ClickAction: this.onClickButton3,
      button1Type: "inline",
      button1Variant: "progressive",
      legacyButton1Variant: "inline-progressive",
      button1ClickAction: assessmentToolHasTemplate && this.onClickToolSave,
      disableButton3: this.shouldDisableButton3(),
    });

    const selectedAssessmentTool = getSelectedAssessmentTool({
      assessmentTools,
      assessmentToolType,
    });

    this.updateHeaderDetails({
      title:
        assessmentToolType === "DP_ASSESSMENT_RUBRIC"
          ? t("common:dp_assessment_criteria_for_label", {
              label: subjectName,
            }) || ""
          : assessmentToolTypeLocale,
      showCloseButton: false,
      subtext:
        assessmentToolType === "DP_ASSESSMENT_RUBRIC"
          ? getDPCriteriaRubricHeaderSubtext({
              assessmentTool: selectedAssessmentTool,
              t,
            })
          : "",
    });
  };

  updateSelectedCriteria = data => {
    this.setState({ selectedCriteria: data });
  };

  renderTool = ({ mode = "edit", assessmentTool }) => {
    const {
      t,
      assessmentToolType,
      setScrollToBottom,
      updateInputField,
      unitPlanFields,
      field_list,
      assessmentFields,
      createNew,
      toolType,
      assessmentToolModalMode,
      portalType,
      testId,
      assessmentId,
    } = this.props;

    const { headerDetails, footerDetails, selectedCriteria } = this.state;

    if (_.isEmpty(assessmentTool)) return;
    const renderToolContent = () => {
      switch (assessmentToolType) {
        case "RUBRIC":
          return (
            <RubricTable
              customRef={this.updateInputRef}
              mode={mode}
              rubric={assessmentTool}
              onClickToolSave={this.onClickToolSave}
              setScrollToBottom={setScrollToBottom}
              showSaveTemplateButton={false}
              actionButtonsUIType={"ICON_BUTTON"}
              showHoverState={mode === "edit"}
              scrollToLastCriteria={true}
              portalType={portalType}
              uiType={"COMPACT"}
              isEnabledAssessmentToolReordering={true}
              testId={`${testId}-assessmentCreationModal`}
            />
          );
        case "CHECKLIST":
          return (
            <ChecklistTable
              mode={mode}
              customRef={this.updateInputRef}
              checklist={assessmentTool}
              onClickToolSave={this.onClickToolSave}
              setScrollToBottom={setScrollToBottom}
              showSaveTemplateButton={false}
              actionButtonsUIType={"ICON_BUTTON"}
              showHoverState={mode === "edit"}
              uiType={"COMPACT"}
              isEnabledAssessmentToolReordering={true}
              portalType={portalType}
            />
          );
        case "SINGLE_POINT_RUBRIC":
          return (
            <SinglePointRubricTable
              mode={mode}
              customRef={this.updateInputRef}
              rubric={assessmentTool}
              onClickToolSave={this.onClickToolSave}
              setScrollToBottom={setScrollToBottom}
              showSaveTemplateButton={false}
              actionButtonsUIType={"ICON_BUTTON"}
              uiType={"COMPACT"}
              isEnabledAssessmentToolReordering={true}
              portalType={portalType}
            />
          );
        case "ANECDOTE":
          return (
            <Anecdotal
              {...this.props}
              options={getAnecdotalOptions()}
              ref={this.updateInputRef}
              anecdotal={assessmentTool?.message}
              onClickToolSave={this.onClickToolSave}
              writeAssessmentToolInLocal={this.writeAssessmentToolInLocal}
              updateAssessmentTool={this.updateAssessmentTool}
              styles={styles.anecdotalStyles}
            />
          );
        case "MYP_OBJECTIVE_RUBRIC":
        case "MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC":
          return (
            <MYPObjectiveRubric
              {...this.props}
              options={getMYPObjectiveRubricOptions({
                unitPlanFields,
                field_list,
                assessmentFields,
                measureAssessingValue: toolType,
              })}
              ref={this.updateInputRef}
              customRef={this.updateInputRef}
              updateInputField={updateInputField}
              isCreateMode={createNew}
              toolType={toolType}
              isMYPObjectiveRubricNewUI={true}
              showOnlyUnitSelections={_.get(
                headerDetails,
                "showOnlyUnitSelections"
              )}
              updateHeaderDetails={this.updateHeaderDetails}
            />
          );
        case "EXEMPLAR":
          return (
            <Exemplar
              {...this.props}
              ref={this.updateInputRef}
              onFocus={this.onFocus}
              onClickToolSave={this.onClickToolSave}
              onBlur={this.onBlur}
              value={assessmentTool?.message}
              attachments={assessmentTool?.attachments}
              createAttachment={this.createAttachment}
              deleteAttachment={this.deleteAttachment}
              writeAssessmentToolInLocal={this.writeAssessmentToolInLocal}
              updateAssessmentTool={this.updateAssessmentTool}
            />
          );
        case "DP_ASSESSMENT_RUBRIC": {
          const headerTitle = _.get(headerDetails, "title");
          const isButton3Disabled = _.get(
            footerDetails,
            "disableButton3",
            false
          );
          const assessmentToolTitle = _.get(assessmentTool, "label");

          if (_.isEmpty(headerTitle) && !_.isEmpty(assessmentToolTitle)) {
            this.updateHeaderDetails({ title: assessmentToolTitle });
          }

          const criteriaPath =
            mode === "edit" ? "presetRubric.criteria" : "criteria";
          const criteria = _.get(assessmentTool, criteriaPath, []);

          const singleLevel = !_.some(
            criteria,
            item => !_.isEqual(item.parent, null)
          );

          const _getIsCriterionSelected = criterionId => {
            return getIsCriterionSelected({ selectedCriteria, criterionId });
          };

          const _getCheckboxState = criterionId => {
            return getCheckboxState({
              selectedCriteria,
              singleLevel,
              criteria,
              criterionId,
            });
          };

          const onClickCheckbox = criterionId => {
            const isCriterionSelected = _getIsCriterionSelected(criterionId);

            const updatedCriteria = getUpdatedCriteria({
              selectedCriteria,
              singleLevel,
              criteria,
              criterionId,
              isCriterionSelected,
            });
            this.updateSelectedCriteria(updatedCriteria);

            if (_.isEmpty(updatedCriteria)) {
              this.updateFooterDetails({
                disableButton3: true,
                button3DisabledTooltip: t(
                  "classRoom:select_atleast_one_criterion_to_tag_rubric"
                ),
              });
            } else if (isButton3Disabled) {
              this.updateFooterDetails({
                disableButton3: false,
                button3DisabledTooltip: "",
              });
            }
          };

          const criteriaData = [
            {
              criteria,
              hasMarksBox: true,
            },
          ];

          const isAssessmentToolCreateMode = _.startsWith(
            assessmentToolModalMode,
            "use_from_template"
          );
          const isAssessmentToolEditMode =
            assessmentToolModalMode === "edit_tool";

          const showCriterionSelection =
            isAssessmentToolCreateMode || isAssessmentToolEditMode;

          return (
            <CriteriaRubricAssessment
              typeOfScale={"unMarked"}
              criteriaData={criteriaData}
              assessmentHeading={_.get(assessmentTool, "label", "")}
              showCriterionSelection={showCriterionSelection}
              getIsCriterionSelected={_getIsCriterionSelected}
              getCheckboxState={_getCheckboxState}
              onClickCheckbox={onClickCheckbox}
              isAssessmentToolEditMode={isAssessmentToolEditMode}
              assessmentTool={assessmentTool}
              selectedCriteria={this.state.selectedCriteria}
              updateSelectedCriteria={this.updateSelectedCriteria}
              showAssessmentLabelAsRootNode={true}
              updateFooterDetails={this.updateFooterDetails}
              sourceFrom={"CLASSROOM"}
              isSelectionModalMode={showCriterionSelection}
            />
          );
        }

        case SCORE_BASED_RUBRIC:
        case STANDARD_BASED_RUBRIC: {
          return (
            <StandardRubric
              mode={mode}
              assessmentTool={assessmentTool}
              onClickToolSave={this.onClickToolSave}
              showSaveTemplateButton={false}
              portalType={portalType}
              testId={`${testId}-assessmentCreationModal`}
              type={assessmentToolType}
              assessmentId={assessmentId}
              isTemplate={_.startsWith(
                assessmentToolModalMode,
                "use_from_template"
              )}
              uiType={"COMPACT"}
            />
          );
        }
        default:
          return null;
      }
    };

    return (
      <div className={classes.renderToolWrapper}>{renderToolContent()}</div>
    );
  };

  updateTemplateSearchText = text => {
    this.setState({ templateSearchText: text });
  };

  updateTemplateCreatedBy = createdBy => {
    this.setState({ templateCreatedBy: createdBy });
  };

  toggleAssessmentToolModal = () => {
    const { showAssessmentToolModal, updateAssessmentToolData } = this.props;
    updateAssessmentToolData({
      showAssessmentToolModal: !showAssessmentToolModal,
    });
  };

  onDialogConfirmClick = async e => {
    const {
      assessmentToolModalMode,
      parentId,
      parentType,
      removeAssessmentTool,
      portalType,
    } = this.props;

    const {
      currentDialogueAction: { type, params },
    } = this.state;

    switch (type) {
      case "SAVE_TEMPLATE":
        this.saveAssessmentTool(e);
        break;
      case "DISCARD": {
        const shouldClearAssessmentTool = _.startsWith(
          assessmentToolModalMode,
          "create_new"
        );

        if (shouldClearAssessmentTool) {
          await removeAssessmentTool({
            id: params?.toolId,
            type: params?.toolType,
            parentId,
            parentType,
            portalType,
          });

          this.toggleAssessmentToolModal();
        } else {
          this.toggleAssessmentToolModal();
        }
      }
    }
  };

  updateCurrentDialogueBoxAction = params => {
    this.setState({ currentDialogueAction: params });
  };

  removeTool = async ({ toolId, toolType, shouldToggleModal = true }) => {
    const {
      parentId,
      parentType,
      removeAssessmentTool,
      portalType,
    } = this.props;

    await removeAssessmentTool({
      id: toolId,
      type: toolType,
      parentId,
      parentType,
      portalType,
    });

    if (shouldToggleModal) {
      this.toggleAssessmentToolModal();
    }
  };

  render() {
    const {
      t,
      type,
      toolId,
      assessmentTool,
      assessmentToolModalMode,
      assessmentToolMode,
      assessmentToolType,
      isDPRubric,
      createNew,
      mode,
      isTOK,
      isInterdisciplinaryRubric,
      unitPlan,
      assignmentId,
      registerAssessmentToolUpdateActivity,
      testId,
      isLearningCoursesEnabled,
      learningCourseId,
      bulkCreateStandardRubricCriteria,
      assessmentToolTemplateUserFilterPreference,
      assessmentFields,
      assessmentId,
      assessmentTools,
    } = this.props;

    const {
      headerDetails,
      footerDetails,
      currentDialogueAction,
      templateSearchText,
      templateCreatedBy,
      selectedCriteria,
      filterConfig,
      showLearningCourseTools,
      convertedScoreBasedRubricId,
    } = this.state;

    const { showFooter } = footerDetails;

    const assessmentToolName = t(
      _.get(
        _.find(ASSESSMENT_TOOLS, { type: assessmentToolType }),
        "locale",
        ""
      )
    );

    const isAssessmentToolEmpty = getIsAssessmentToolEmpty({
      assessmentToolType,
      assessmentTool,
    });

    const handleBlur = () => {
      registerAssessmentToolUpdateActivity({
        assignmentId,
        toolType: assessmentToolType,
      });
    };

    const isCreateOrViewMode =
      _.startsWith(assessmentToolModalMode, "create_new") ||
      _.startsWith(assessmentToolModalMode, "view_tool");

    const renderTool = () => {
      if (isCreateOrViewMode) {
        return _.isEmpty(assessmentTool) ? (
          <Loading />
        ) : (
          this.renderTool({ assessmentTool, mode: assessmentToolMode })
        );
      } else if (_.startsWith(assessmentToolModalMode, "use_from_template")) {
        return (
          <AssessmentToolChooseListV2
            type={assessmentToolType}
            entityType={type}
            name={assessmentToolName}
            searchText={templateSearchText}
            createdBy={templateCreatedBy}
            selectedCriteria={selectedCriteria}
            updateSelectedCriteria={this.updateSelectedCriteria}
            renderTool={this.renderTool}
            onCopyTool={this.onCopyTool}
            updateHeaderDetails={this.updateHeaderDetails}
            updateFooterDetails={this.updateFooterDetails}
            handleTemplateSearch={this.updateTemplateSearchText}
            toggleAssessmentToolModal={this.toggleAssessmentToolModal}
            updateTemplateCreatedBy={this.updateTemplateCreatedBy}
            isTOK={isTOK}
            filterConfig={filterConfig}
            updateFilterConfig={this.updateFilterConfig}
            testId={`${testId}-assessmentCreationModal`}
            showLearningCourseTools={showLearningCourseTools}
            learningCourseId={learningCourseId}
            isLearningCoursesEnabled={isLearningCoursesEnabled}
            assessmentToolTemplateUserFilterPreference={
              assessmentToolTemplateUserFilterPreference
            }
            assessmentFields={assessmentFields}
            assessmentId={assessmentId}
            convertedScoreBasedRubricId={convertedScoreBasedRubricId}
            onChangeConvertedScoreBasedRubricId={
              this.onChangeConvertedScoreBasedRubricId
            }
            removeTool={this.removeTool}
            assessmentTools={assessmentTools}
          />
        );
      } else {
        return (
          assessmentTool &&
          this.renderTool({ assessmentTool, mode: assessmentToolMode })
        );
      }
    };

    if (getIsIBGradeScaleTool({ assessmentToolType, checkForManual: false })) {
      const IBScaleTool =
        this.props.isUsingNewGradingSetupInCourses &&
        getIsIBGradeScaleV2ForNewGradingSetupEnabled()
          ? AutomaticIBScaleToolV2
          : AutomaticIBScaleTool;
      const resolvedCategory = getCategoryObj({
        globalCategories: this.props.globalCategories,
        category: this.props.category,
        isUsingNewGradingSetupInCourses: this.props
          .isUsingNewGradingSetupInCourses,
      });
      return (
        <IBScaleTool
          {...this.props}
          category={resolvedCategory}
          toggleAssessmentToolModal={this.toggleAssessmentToolModal}
          showModalInViewModal={mode === "view"}
        />
      );
    } else if (
      assessmentToolType === STANDARD_BASED_RUBRIC &&
      _.startsWith(assessmentToolModalMode, "create_new")
    ) {
      const onSelect = async ({
        selectedStandards: selectedStandardsObj,
        selectedGradeScale,
        elementType: elementTypeObj,
      }) => {
        // Flatten per-set objects to the flat shapes expected downstream.
        const selectedStandards = Object.values(selectedStandardsObj).flat();
        const elementType = _.values(elementTypeObj)[0];

        const gradeScaleId = _.get(selectedGradeScale, "id");

        // create assessment tool
        const toolId = await this.createAssessmentTool({
          extraParams: { gradeScaleId },
        });

        await bulkCreateStandardRubricCriteria({
          input: _.map(selectedStandards, (id, index) => ({
            elementId: id,
            gradeScaleId,
            toolId,
            displaySequence: index + 1,
            elementType,
          })),
          type: STANDARD_BASED_RUBRIC,
        });

        this.toggleAssessmentToolModal();
      };

      return (
        <StandardsSelectionModal
          onClose={this.toggleAssessmentToolModal}
          onSelectClick={onSelect}
          learningCourseId={learningCourseId}
          assessmentFields={assessmentFields}
          assessmentId={assessmentId}
          canMapGradeScale={true}
        />
      );
    } else if (assessmentToolType === GRADE_SCALE_TOOL) {
      const toolId = _.get(assessmentTool, "id");

      return (
        <GradeScaleDetailModal
          toolId={toolId}
          onClose={this.toggleAssessmentToolModal}
        />
      );
    }

    const isMYPObjectiveRubricNewUI =
      _.includes(
        ["MYP_OBJECTIVE_RUBRIC", "MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC"],
        assessmentToolType
      ) && assessmentToolModalMode !== "view_tool";

    const containerClasses = classNames({
      [classes.container]: true,
      [classes.objectiveRubricContainer]: isMYPObjectiveRubricNewUI,
    });

    const innerContainerClasses = classNames({
      [classes.innerContainer]: !isMYPObjectiveRubricNewUI,
      [classes.objectiveRubricInnerContainer]: isMYPObjectiveRubricNewUI,
    });

    return (
      <>
        <UIModal modalContent={classes.modalContent} isOpen={true}>
          <Header
            t={t}
            headerDetails={headerDetails}
            toolId={toolId}
            toolType={assessmentToolType}
            isDPRubric={isDPRubric}
            updateCurrentDialogueBoxAction={this.updateCurrentDialogueBoxAction}
            isAssessmentToolEmpty={isAssessmentToolEmpty}
            removeTool={this.removeTool}
            toggleAssessmentToolModal={this.toggleAssessmentToolModal}
            createNew={createNew}
            isMYPObjectiveRubricNewUI={isMYPObjectiveRubricNewUI}
            updateHeaderDetails={this.updateHeaderDetails}
            isInterdisciplinaryRubric={isInterdisciplinaryRubric}
            unitPlan={unitPlan}
            testId={testId}
            isLearningCoursesEnabled={isLearningCoursesEnabled}
            handleLearningCourseToggle={this.handleLearningCourseToggle}
            showLearningCourseTools={showLearningCourseTools}
            convertedScoreBasedRubricId={convertedScoreBasedRubricId}
          />

          <div className={containerClasses} onBlur={handleBlur}>
            <div className={innerContainerClasses}>{renderTool()}</div>
          </div>

          {showFooter && (
            <Footer
              t={t}
              footerDetails={footerDetails}
              onClickToolSave={this.onClickToolSave}
              toggleAssessmentToolModal={this.toggleAssessmentToolModal}
              testId={testId}
            />
          )}
        </UIModal>

        {!!currentDialogueAction.type && (
          <DialogueBoxV2
            modalTitle={DIALOGUES_INFO[currentDialogueAction.type].title({
              t,
              label: assessmentToolName,
            })}
            showModal={true}
            onClickButton2={this.onDialogConfirmClick}
            modalBody={DIALOGUES_INFO[currentDialogueAction.type].message({
              t,
              label: assessmentToolName,
            })}
            toggleDialogueBoxDisplay={() =>
              this.setState({ currentDialogueAction: { type: "" } })
            }
            button1={DIALOGUES_INFO[
              currentDialogueAction.type
            ].cancelButtonText(t)}
            button2={DIALOGUES_INFO[
              currentDialogueAction.type
            ].confirmButtonText(t)}
            headerStyles={styles.headerStyles}
            modalBodyStyle={styles.modalBodyStyle}
            closeButtonSize={"medium"}
            isPromptBox={currentDialogueAction.type === "SAVE_TEMPLATE"}
            promptDetail={getPromptDetail(t)}
            button1Props={styles.button1Props}
            size={"sm"}
          />
        )}
      </>
    );
  }
}

const Header = props => {
  const {
    t,
    headerDetails,
    isDPRubric,
    updateCurrentDialogueBoxAction,
    isAssessmentToolEmpty,
    removeTool,
    createNew,
    toggleAssessmentToolModal,
    isMYPObjectiveRubricNewUI,
    updateHeaderDetails,
    isInterdisciplinaryRubric,
    unitPlan,
    testId,
    isLearningCoursesEnabled,
    handleLearningCourseToggle,
    showLearningCourseTools,
    convertedScoreBasedRubricId,
  } = props;

  const {
    title,
    subtext,
    showBackButton,
    showCloseButton,
    backButtonClickAction,
    disableCloseButton,
    showOnlyUnitSelections,
    isTemplateSelectionStep,
  } = headerDetails;

  const handleToggleSwitch = () => {
    updateHeaderDetails({
      showOnlyUnitSelections: !showOnlyUnitSelections,
    });
  };

  const onCancelIconClick = async () => {
    const { toolId, toolType } = props;

    // once rubric is converted to score based rubric, it also gets added to the assessment
    // hence if user closes the modal and tool was converted then delete the converted score based rubric
    if (convertedScoreBasedRubricId) {
      await removeTool({ toolId: convertedScoreBasedRubricId, toolType });
      return;
    }

    if (createNew) {
      if (isAssessmentToolEmpty) {
        await removeTool({ toolId, toolType });
      } else {
        updateCurrentDialogueBoxAction({
          type: "DISCARD",
          params: {
            toolId,
            toolType,
          },
        });
      }
    } else {
      toggleAssessmentToolModal();
    }
  };

  const renderTitle = () => {
    if (title) {
      if (isDPRubric) return title;
      else {
        return t("common:capitalize", {
          text: title,
        });
      }
    }
  };

  const headerContainerClass = classNames({
    [classes.headerContainer]: true,
    [classes.headerContainerWithBackButton]: showBackButton,
  });

  return (
    <div className={headerContainerClass}>
      <div className={classes.leftContainer}>
        {showBackButton && (
          <IconButton
            dsVersion="2.0"
            variant="neutral"
            type="plain"
            size="large"
            icon={<ChevronLeftOutlined />}
            onClick={backButtonClickAction}
            testId={`${testId}-assessmentCreationModal-back`}
          />
        )}
        <div className={classes.leftContainerText}>
          <div className={classes.headerTitle}>{renderTitle()}</div>
          {subtext && isDPRubric && (
            <div className={classes.subtext}>{subtext}</div>
          )}
        </div>
        {/* temporarily hiding the toggle button for learning course tools */}
        {/* {isLearningCoursesEnabled && isTemplateSelectionStep ? (
          <div className={classes.toggleButtonContainer}>
            <div className={classes.separator}></div>
            <div className="text-body">
              {t("classRoom:only_show_course_assessment_tools")}
            </div>
            <ToggleSwitch
              checked={showLearningCourseTools}
              onChange={handleLearningCourseToggle}
            />
          </div>
        ) : null} */}
      </div>
      <div className={classes.headerRightContainer}>
        {isMYPObjectiveRubricNewUI && !_.isEmpty(unitPlan) ? (
          <div className={classes.toggleButtonContainer}>
            <div className="text-body-1">
              {t("classRoom:only_show_label_selected_in_this_unit", {
                label: isInterdisciplinaryRubric
                  ? t("common:interdisciplinary_criteria")
                  : t("common:myp_criteria"),
              })}
            </div>
            <ToggleSwitch
              checked={showOnlyUnitSelections}
              onChange={handleToggleSwitch}
            />
          </div>
        ) : null}
        {showCloseButton ? (
          <IconButton
            dsVersion="2.0"
            icon={<CloseOutlined size={"xx-small"} />}
            variant="neutral"
            type="plain"
            onClick={onCancelIconClick}
            testId={`${testId}-assessmentCreationModal-cancel`}
            disabled={disableCloseButton}
          />
        ) : null}
      </div>
    </div>
  );
};

const Footer = props => {
  const { footerDetails, testId } = props;

  const {
    showButton1,
    showButton2,
    showButton3,
    button1Label,
    button2Label,
    button3Label,
    button1ClickAction,
    button2ClickAction,
    button3ClickAction,
    button1Variant,
    button1Type,
    button3Variant,
    disableButton2,
    disableButton3,
    button3DisabledTooltip,
  } = footerDetails;

  return (
    <div className={classes.footerContainer}>
      <div className={classes.buttonsContainer}>
        <div className={classes.leftButtonContainer}>
          {showButton1 && (
            <Button
              dsVersion="2.0"
              size="medium"
              variant={button1Variant}
              type={button1Type}
              onClick={button1ClickAction}
              testId={`${testId}-assessmentCreationModal-${_.camelCase(
                button1Label
              )}`}
            >
              {button1Label}
            </Button>
          )}
        </div>

        <div className={classes.rightButtonContainer}>
          {showButton2 && (
            <Button
              dsVersion="2.0"
              size="medium"
              variant="neutral"
              type="outlined"
              onClick={button2ClickAction}
              testId={`${testId}-assessmentCreationModal-${_.camelCase(
                button2Label
              )}`}
              disabled={disableButton2}
            >
              {button2Label}
            </Button>
          )}
          {showButton3 && (
            <ConditionalWrapper
              type={"wrapperAsElement"}
              condition={disableButton3 && !_.isEmpty(button3DisabledTooltip)}
              wrapper={
                <Tooltip
                  tooltip={button3DisabledTooltip}
                  placement="left"
                  showArrow={true}
                />
              }
            >
              <Button
                dsVersion="2.0"
                size="medium"
                variant={button3Variant}
                onClick={button3ClickAction}
                testId={`${testId}-assessmentCreationModal-${_.camelCase(
                  button3Label
                )}`}
                disabled={disableButton3}
              >
                {button3Label}
              </Button>
            </ConditionalWrapper>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const userInfo = getUserInfo();
  const { t, assessmentToolData, assessmentFields } = ownProps;

  const {
    assessmentId,
    assessmentToolId,
    assessmentToolType,
  } = state.assessment.assessmentData;

  const {
    showAssessmentToolModal,
    assessmentToolModalMode,
    assessmentToolMode,
  } = assessmentToolData || {};

  const type = _.get(
    _.find(ASSESSMENT_TOOLS, { type: assessmentToolType }),
    "entityType",
    ""
  );

  const mode =
    ownProps.mode == "view" ? "view" : !assessmentToolId ? "create" : "edit";

  const organizationId = userInfo.org_id;
  const userId = userInfo.id;
  const { id: curriculumProgramId } = state.platform.currentCurriculumProgram;

  const createNew = _.startsWith(assessmentToolModalMode, "create_new");
  const isCreateMode = _.isEqual(mode, "create") && createNew;

  const isDPRubric = assessmentToolType === "DP_ASSESSMENT_RUBRIC";

  const assessmentToolTypeLocale = t(
    _.find(ASSESSMENT_TOOLS, { type: assessmentToolType })?.locale
  );

  const assessmentToolHasTemplate = _.find(ASSESSMENT_TOOLS, {
    type: assessmentToolType,
  })?.hasTemplate;

  const courseId = _.get(state, "teacher.selected_class.selected_course", "");
  const academicYearId = getSelectedAcademicYearId(state);
  const courseGradeFilters = {
    academicYearId,
  };
  const courseDetails = getCourseDetailsFromCache({
    id: courseId,
    courseGradeFilters,
  });
  let subjectName = _.get(
    _.first(_.get(courseDetails, "subjects", [])),
    "name",
    ""
  );

  if (getIsLearningCoursesEnabled()) {
    const learningCourseDetails = getAssessmentLearningCourseDetailsFromCache({
      id: _.get(ownProps, "learningCourseId"),
    });
    subjectName = _.get(
      _.first(_.get(learningCourseDetails, "subjects", [])),
      "name",
      ""
    );
  }

  const isTOK = _.get(ownProps, "currentSubject.0.type") === "TOK";

  const unitPlan = getFieldValue({ fields: assessmentFields, uid: "unitPlan" });
  const isInterdisciplinaryRubric =
    assessmentToolType === MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC;

  const isLearningCoursesEnabled = getIsLearningCoursesEnabled();

  const assessmentToolTemplateUserFilterPreference = getSettingValue({
    userId,
    readFromUserAndOrgLevel: true,
    name: "AssessmentToolTemplateUserFilterPreference",
  });

  return {
    assessmentId: ownProps.assessmentId ? ownProps.assessmentId : assessmentId,
    toolId: assessmentToolId,
    mode,
    type,
    assessmentToolType,
    assessmentToolTypeLocale,
    assessmentToolHasTemplate,
    isCreateMode,
    organizationId,
    userId,
    createNew,
    isData: true,
    isLoading: false,
    curriculumProgramId,
    assessmentToolMode,
    showAssessmentToolModal,
    assessmentToolModalMode,
    isComment: type === "COMMENT",
    isDPRubric,
    toolType: assessmentToolType,
    subjectName,
    isTOK,
    unitPlan,
    isInterdisciplinaryRubric,
    isLearningCoursesEnabled,
    assessmentToolTemplateUserFilterPreference,
  };
};

const mapActionCreators = {
  writeAssessmentToolInLocal,
  updateAssessmentTool,
  createAssessmentTool,
  deleteAssessmentTool,
  createAttachment,
  deleteAttachment,
  updateField,
  removeAssessmentTool,
  updateAssessmentInRedux,
  upsertDpAssessmentRubricsV2InAssessment,
  registerAssessmentToolUpdateActivity,
  bulkCreateStandardRubricCriteria,
  updateSettings,
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getAssessmentToolNodeDetailsQuery, {
    name: "getAssessmentToolNodeDetails",
    skip: ({ toolId, type, assessmentToolType, mode }) =>
      !toolId ||
      _.includes(toolId, "NEW") ||
      _.includes(SKIP_ASSESSMENT_TOOL_NODE_TOOLS, type) ||
      (mode === "edit" &&
        [DP_ASSESSMENT_RUBRIC, STANDARD_BASED_RUBRIC].includes(
          assessmentToolType
        )),
    options: ({ toolId, type, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: toolId, type, portalType },
    }),
    props({
      getAssessmentToolNodeDetails,
      ownProps: { isData, isLoading, toolId, type },
    }) {
      const queryData = getAssessmentToolNodeDetailsFromCache({
        id: toolId,
        type,
      });
      const networkStatus = _.get(
        getAssessmentToolNodeDetails,
        "networkStatus"
      );
      return {
        assessmentTool: queryData,
        isData: !_.isEmpty(queryData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  graphql(getDpAssessmentRubricQuery, {
    name: "getDpAssessmentRubric",
    skip: ({ assessmentToolType, mode }) =>
      mode !== "edit" || assessmentToolType !== "DP_ASSESSMENT_RUBRIC",
    options: ({ toolId, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: toolId, portalType },
    }),
    props({ getDpAssessmentRubric, ownProps: { isData, isLoading, toolId } }) {
      const queryData = getDpAssessmentRubricFromCache({
        id: toolId,
      });
      const networkStatus = _.get(getDpAssessmentRubric, "networkStatus");
      return {
        assessmentTool: queryData,
        isData: !_.isEmpty(queryData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  })
)(AssessmentToolV2);
